import { Link } from 'react-router'
import { FileWarning } from 'lucide-react'
import { EmptyState } from '@/components/common'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useLookup } from '@/hooks/useLookup'
import { DocumentStatusBadge } from './DocumentStatusBadge'
import { useExpiringCompanyDocuments } from '../api'
import type { CompanyDocumentView } from '../types'

/**
 * What is about to run out, on the dashboard.
 *
 * <p>Company documents only, and the heading says so. A trade licence lapsing stops the
 * whole entity trading, so it belongs on a screen everybody sees; an individual's passport
 * belongs on their own record and in the email the sweep sends them. Putting every
 * employee's expiring visa on a shared dashboard would be a privacy decision made by
 * accident.
 *
 * <p>Expired first, then soonest. The ordering is the point of the widget: a list sorted by
 * name is a list nobody acts on.
 */
export function ExpiringDocumentsCard({ limit = 5 }: { limit?: number }) {
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const entityIds = (entities.data ?? []).map((entity) => entity.id)
  const documents = useExpiringCompanyDocuments(entityIds)

  const urgent = documents.data
    .filter((document) => document.status === 'EXPIRED' || document.status === 'EXPIRING')
    .sort(byUrgency)
    .slice(0, limit)

  return (
    <Card className="p-5">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-[var(--color-heading)]">
          <FileWarning className="size-4 text-[var(--color-warning)]" aria-hidden="true" />
          Company documents expiring
        </h2>
        <Link
          to="/documents/company"
          className="text-xs font-medium text-[var(--color-primary)] hover:underline"
        >
          See all
        </Link>
      </div>

      {(entities.isPending || documents.isPending) && <Skeleton className="h-24" />}
      {!documents.isPending && urgent.length === 0 && (
        <EmptyState
          title="Nothing expiring"
          description="Every licence and card on file is comfortably in date."
        />
      )}
      {urgent.length > 0 && (
        <ul className="space-y-2.5">
          {urgent.map((document) => (
            <li key={document.id} className="flex flex-wrap items-center justify-between gap-2">
              <span className="min-w-0 text-sm">
                <span className="font-medium text-[var(--color-heading)]">
                  {document.documentTypeName}
                </span>
                {document.documentNumber && (
                  <span className="text-[var(--color-muted)]"> · {document.documentNumber}</span>
                )}
              </span>
              <DocumentStatusBadge
                status={document.status}
                daysUntilExpiry={document.daysUntilExpiry}
                withPhrase
              />
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

/** Expired first, then whichever runs out soonest. */
function byUrgency(left: CompanyDocumentView, right: CompanyDocumentView): number {
  return (left.daysUntilExpiry ?? Number.MAX_SAFE_INTEGER) - (right.daysUntilExpiry ?? Number.MAX_SAFE_INTEGER)
}
